import { FC, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { Box, chakra, VStack } from '@chakra-ui/react';
import { CardNumberElement, useElements } from '@stripe/react-stripe-js';
import { StripeCardNumberElement } from '@stripe/stripe-js';
import { motion } from 'framer-motion';

import { PackageType } from '__generated__/GQL';
import asBentoPage from 'common/bento/hoc/asBentoPage';
import SunshineIcon from 'components/_core/SunshineIcon';
import Text from 'components/_core/Text';
import SuspenseQuery from 'components/SuspenseQuery';
import Header from 'features/Onboarding/components/Header';
import OBNavigation from 'features/Onboarding/components/OBNavigation/OBNavigation';
import Form from 'features/Onboarding/legacy/components/PriceConfirmationAndPaymentPage/PaymentPageContent/Form';
import amountWithMinorCurrency from 'features/Onboarding/modules/PricingPlan/helpers/amountWithMinorCurrency';
import formatPrice from 'features/Onboarding/modules/PricingPlan/pages/PackageRecap/lib/formatPrice';

import {
  OnboardingSasuOffersDocument,
  OnboardingSasuOffersQuery,
} from '../../graphql/queries/onboardingSasuOffers.gql';
import { OnboardingPackageItem } from '../../machine/model';
import locales from './locales';

type SasuAddon =
  OnboardingSasuOffersQuery['viewer']['company']['onboardingSasuAddons'][0];

interface PackagePaymentProps {
  pack: OnboardingPackageItem;
  isSubmittingPayment: boolean;
  onPay: (cardElement: StripeCardNumberElement) => void;
  addons: SasuAddon[];
}

const PackagePayment: FC<PackagePaymentProps> = ({
  addons,
  isSubmittingPayment,
  onPay,
  pack,
}) => {
  const elements = useElements();

  const {
    'sasu-administrative-fees': administrativeFeesAddon,
    'sasu-formalities': sasuFormalitiesAddon,
  } = useMemo(
    () =>
      addons.reduce(
        (obj, { providerAddonId, ...rest }) => ({
          ...obj,
          [providerAddonId]: rest,
        }),
        {} as {
          'capital-deposit'?: Omit<SasuAddon, 'providerAddonId'>;
          'sasu-administrative-fees'?: Omit<SasuAddon, 'providerAddonId'>;
          'sasu-formalities'?: Omit<SasuAddon, 'providerAddonId'>;
        },
      ),
    [addons],
  );

  if (!administrativeFeesAddon || !sasuFormalitiesAddon) {
    throw new Error('Missing addon information');
  }

  const price =
    pack.discountPrice +
    administrativeFeesAddon.basePrice +
    sasuFormalitiesAddon.basePrice;

  return (
    <Box>
      <Form
        disabled={isSubmittingPayment}
        loading={isSubmittingPayment}
        onSubmit={async (event) => {
          event.preventDefault();
          const cardElement = elements?.getElement(CardNumberElement);

          if (!cardElement) {
            throw new Error('Card element not found');
          }

          onPay(cardElement);
        }}
        price={amountWithMinorCurrency(price)}
      />

      <VStack align="stretch" marginTop="space-40">
        <Text fontWeight="weight-500" size="sm">
          <SunshineIcon name="lock" />

          <chakra.span marginLeft="space-4">{locales.footer.label}</chakra.span>
        </Text>

        <Text size="sm">
          {locales.formatString(locales.footer.details, {
            planName: `Shine ${pack.plan.brandName}`,
            pricePerMonth: formatPrice(pack.plan.priceWithoutVAT),
            trialPeriod: pack.trialPeriod,
          })}
        </Text>
      </VStack>
    </Box>
  );
};

interface PackagePaymentContainerProps {
  providerPackageId: string;
  companyProfileId: string;
  onPrev: () => void;
  isSubmittingPayment: boolean;
  onPay: (cardElement: StripeCardNumberElement) => void;
}

const PackagePaymentContainer = asBentoPage<PackagePaymentContainerProps>(
  ({ companyProfileId, Layout, onPrev, providerPackageId, ...props }) => {
    const query = useQuery(OnboardingSasuOffersDocument, {
      variables: {
        companyProfileId,
        input: {
          type: PackageType.CapitalDeposit,
        },
      },
    });

    return (
      <Layout nav={<OBNavigation onPrev={onPrev} />}>
        <Header title={locales.title} />

        <SuspenseQuery query={query}>
          {({ viewer: { company } }) => {
            const pack = company.onboardingPackages.find(
              (p) => p.providerPackageId === providerPackageId,
            );
            const { onboardingSasuAddons } = company;

            if (!pack) {
              throw new Error('Package not found');
            }

            return (
              <motion.div
                animate={{ opacity: 1, y: 0 }}
                initial={{ opacity: 0, y: 20 }}
              >
                <PackagePayment
                  {...props}
                  addons={onboardingSasuAddons}
                  pack={pack as OnboardingPackageItem}
                />
              </motion.div>
            );
          }}
        </SuspenseQuery>
      </Layout>
    );
  },
);

export default PackagePaymentContainer;
